import { 
  collection, 
  addDoc, 
  serverTimestamp,
  query,
  where,
  orderBy,
  getDocs,
  limit
} from 'firebase/firestore';
import { db } from '../firebase/config';

export const contactService = {
  // Send contact message to Firestore
  async sendMessage(messageData) {
    try {
      // Prepare message for Firestore
      const firestoreMessage = {
        name: messageData.name,
        email: messageData.email,
        subject: messageData.subject,
        category: messageData.category,
        message: messageData.message,
        systemInfo: messageData.systemInfo || null,
        userType: messageData.systemInfo?.userType || 'unknown',
        userId: messageData.systemInfo?.userId || 'anonymous',
        timestamp: serverTimestamp(),
        createdAt: serverTimestamp(),
        status: 'new', // new, read, replied
        priority: this.getPriority(messageData.category),
        userAgent: messageData.systemInfo?.userAgent || null,
        platform: messageData.systemInfo?.platform || null,
        language: messageData.systemInfo?.language || null
      };

      // Save to Firestore
      const docRef = await addDoc(collection(db, 'contactMessages'), firestoreMessage);
      
      console.log('Message saved to Firestore with ID:', docRef.id);
      
      // Also save locally as backup
      this.saveMessageLocally({
        ...messageData,
        id: docRef.id,
        firestoreId: docRef.id
      });
      
      return { 
        success: true, 
        id: docRef.id,
        message: 'Mesajul a fost trimis cu succes!'
      };
      
    } catch (error) {
      console.error('Error sending contact message to Firestore:', error);
      
      // Fallback to local save if Firestore fails
      this.saveMessageLocally({
        ...messageData,
        id: Date.now().toString(),
        firestoreError: error.message
      });
      
      throw new Error('Eroare la trimiterea mesajului. Te rog încearcă din nou.');
    }
  },

  // Get priority based on category
  getPriority(category) {
    const priorities = {
      'bug': 'high',
      'help': 'medium',
      'feature': 'medium', 
      'suggestion': 'low',
      'other': 'low'
    };
    return priorities[category] || 'low';
  },

  // Save message locally as backup
  saveMessageLocally(messageData) {
    try {
      const messages = JSON.parse(localStorage.getItem('umfcd_contact_messages') || '[]');
      messages.push({
        ...messageData,
        id: messageData.id || Date.now().toString(),
        localTimestamp: new Date().toISOString(),
        savedLocally: true
      });
      
      // Keep only last 10 messages
      if (messages.length > 10) {
        messages.splice(0, messages.length - 10);
      }
      
      localStorage.setItem('umfcd_contact_messages', JSON.stringify(messages));
      
    } catch (error) {
      console.error('Error saving message locally:', error);
    }
  },

  // Get locally saved messages (for debugging)
  getLocalMessages() {
    try {
      return JSON.parse(localStorage.getItem('umfcd_contact_messages') || '[]');
    } catch (error) {
      console.error('Error getting local messages:', error);
      return [];
    }
  },

  // Admin function to get all contact messages (for your use)
  async getAllMessages(limitCount = 50) {
    try {
      const q = query(
        collection(db, 'contactMessages'),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      const messages = [];

      querySnapshot.forEach((doc) => {
        messages.push({
          id: doc.id,
          ...doc.data(),
          // Convert Firestore timestamp to readable date
          timestamp: doc.data().timestamp?.toDate?.() || new Date(),
          createdAt: doc.data().createdAt?.toDate?.() || new Date()
        });
      });

      return messages;
    } catch (error) {
      console.error('Error fetching contact messages:', error);
      throw error;
    }
  },

  // Get messages by status
  async getMessagesByStatus(status = 'new', limitCount = 25) {
    try {
      const q = query(
        collection(db, 'contactMessages'),
        where('status', '==', status),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      const messages = [];

      querySnapshot.forEach((doc) => {
        messages.push({
          id: doc.id,
          ...doc.data(),
          timestamp: doc.data().timestamp?.toDate?.() || new Date(),
          createdAt: doc.data().createdAt?.toDate?.() || new Date()
        });
      });

      return messages;
    } catch (error) {
      console.error('Error fetching messages by status:', error);
      throw error;
    }
  },

  // Get statistics about contact messages
  async getMessageStats() {
    try {
      const messages = await this.getAllMessages(1000); // Get more for stats
      
      const stats = {
        total: messages.length,
        byCategory: {},
        byStatus: {},
        byPriority: {},
        byUserType: {},
        recentCount: 0
      };

      // Calculate stats
      const lastWeek = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      
      messages.forEach(message => {
        // By category
        stats.byCategory[message.category] = (stats.byCategory[message.category] || 0) + 1;
        
        // By status
        stats.byStatus[message.status] = (stats.byStatus[message.status] || 0) + 1;
        
        // By priority
        stats.byPriority[message.priority] = (stats.byPriority[message.priority] || 0) + 1;
        
        // By user type
        stats.byUserType[message.userType] = (stats.byUserType[message.userType] || 0) + 1;
        
        // Recent messages (last week)
        if (message.timestamp > lastWeek) {
          stats.recentCount++;
        }
      });

      return stats;
    } catch (error) {
      console.error('Error getting message stats:', error);
      return null;
    }
  }
};
